import type { DocNode } from '@cca/corpus-shared';

/** Midpoint of two fractional order indices — the drag drop's spacing rule. */
const mid = (a: number, b: number) => (a + b) / 2;

/**
 * Keyboard reorder maths for the block editor's Move up / Move down buttons.
 *
 * Given a block's siblings (same parent, already sorted by `orderIndex`, as
 * `childrenOf` returns them) and the moving block's id, returns the new
 * fractional `orderIndex` that shifts it one slot in `dir` — or `null` when it
 * is already at that boundary (a no-op; the button is disabled there). The
 * fractional-midpoint spacing is the SAME rule the drag drop uses, so keyboard
 * and pointer reorders are interchangeable and land through one persistence
 * path (`move()` in BlockEditor). Pure — no state, no side effects.
 */
export function reorderIndex(
  siblings: DocNode[],
  nodeId: string,
  dir: 'up' | 'down',
): number | null {
  const idx = siblings.findIndex((s) => s.id === nodeId);
  if (idx === -1) return null;
  if (dir === 'up') {
    if (idx === 0) return null; // already first — no-op
    const prev = siblings[idx - 1];
    if (!prev) return null;
    const before = siblings[idx - 2];
    return before ? mid(before.orderIndex, prev.orderIndex) : prev.orderIndex - 1;
  }
  if (idx >= siblings.length - 1) return null; // already last — no-op
  const next = siblings[idx + 1];
  if (!next) return null;
  const after = siblings[idx + 2];
  return after ? mid(next.orderIndex, after.orderIndex) : next.orderIndex + 1;
}
